/**
 * consent — client-side consent state for third-party trackers.
 *
 * Feb 2026 Lane E. Ships DARK — the whole module is a no-op path
 * when `REACT_APP_CONSENT_GATING_ENABLED` is unset/false, matching
 * today's launch posture (US/Omaha only, no consent gate required).
 * The plumbing is here so we can flip the flag when EU / CA
 * expansion or significant paid spend arrives.
 *
 * Scope of the consent gate
 * -------------------------
 * ONLY third-party trackers (currently Meta Pixel). The first-party
 * beacon (`POST /api/beacon/event`) is EXPLICITLY NOT gated — it's
 * designed to be legally defensible without consent (cookieless,
 * daily-rotating hash, no raw IP, no cross-day linkability). Gating
 * the beacon would undermine its legal-neutrality property.
 *
 * Storage
 * -------
 * `localStorage["pp_consent_v1"]` — JSON blob:
 *   { status: "granted" | "denied" | "unset", updated_at, version: 1 }
 * Per-browser (localStorage isn't cross-device; that's fine — GDPR
 * consent is scoped to the browser session anyway).
 *
 * The version field lets us invalidate stale decisions if the
 * privacy policy scope changes materially. Bump `CONSENT_VERSION`
 * and every user re-sees the banner on their next visit.
 */

export const CONSENT_KEY = "pp_consent_v1";
export const CONSENT_VERSION = 1;

/**
 * True iff the build was compiled with the consent-gating feature
 * flag turned on. Read once at module scope? No — read on each call
 * because CRA can evaluate the env at test time to different values.
 */
export function isConsentGatingEnabled() {
  return process.env.REACT_APP_CONSENT_GATING_ENABLED === "true";
}

function _unsetPayload() {
  return { status: "unset", updated_at: null, version: CONSENT_VERSION };
}

/**
 * Read the current consent state. Returns an "unset" payload on
 * missing / malformed / version-mismatched storage.
 */
export function getConsent() {
  if (typeof window === "undefined") return _unsetPayload();
  let raw;
  try {
    raw = window.localStorage.getItem(CONSENT_KEY);
  } catch {
    // Safari private mode etc — treat as unset (safe default).
    return _unsetPayload();
  }
  if (!raw) return _unsetPayload();
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return _unsetPayload();
    if (parsed.version !== CONSENT_VERSION) return _unsetPayload();
    if (!["granted", "denied", "unset"].includes(parsed.status)) {
      return _unsetPayload();
    }
    return parsed;
  } catch {
    return _unsetPayload();
  }
}

/**
 * Persist a new consent status. Returns the written payload for
 * caller convenience. Silently swallows storage errors — a private-
 * mode Safari user can still click through the banner; the "unset"
 * state will just re-render on next page load.
 */
export function setConsent(status) {
  if (!["granted", "denied"].includes(status)) {
    throw new Error(`invalid consent status: ${status}`);
  }
  const payload = {
    status,
    updated_at: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
  } catch {
    // Swallow — caller state (in-memory) will still reflect the
    // user's choice within this tab.
  }
  return payload;
}

/**
 * Decision function: SHOULD the Meta Pixel (and any future 3rd
 * party tracker) load right now?
 *
 * Flag OFF  -> true (today's behavior — pixel loads unconditionally
 *              on non-excluded routes).
 * Flag ON   -> only if the user has explicitly granted.
 */
export function shouldPixelLoad() {
  if (!isConsentGatingEnabled()) return true;
  return getConsent().status === "granted";
}
